// import components
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  LinearProgress,
  Tooltip,
  Typography
} from '@mui/material';
import { StyledTile } from 'components/shared/Tile/Tile.styles';
import { Icon } from '@sdb/planning-ui';

// import hooks
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useSelector } from 'hooks';

// import queries & mutations
import {
  useDownloadRealizationReport,
  useRealizeDutyLinesMutation
} from '../queries';

// import types
import { DutyLinesProgress } from '../types';
import { ReportBody } from './RealizationProcedure.types';

// import utils
import {
  addDays,
  format,
  isAfter,
  isBefore,
  isValid,
  parse,
  startOfDay
} from 'date-fns';
import { formatByLocale } from 'utils/date';
import EventBus from 'lib/EventBus';
import { getLastLockedMonth } from 'RestAPIs/last-locked-month';

// import state
import {
  canDownloadReportAtom,
  dateRangeAtom,
  selectedChipAtom
} from '../atoms';

const eventBusUrl = '/maintenance/realize';

const RealizationProcedure = () => {
  const { t } = useTranslation();
  const { hasTravelMovementsFeature, hasFeatureToggleExpenseClaims } =
    useSelector(({ grants }) => grants);
  const { data: lastLockedMonth } = useQuery(['lastLockedMonth'], async () => {
    const { data } = await getLastLockedMonth();
    return data;
  });

  const [closeAllPrevious, setCloseAllPrevious] = useState<boolean>(false);
  const [dateRange] = useRecoilState(dateRangeAtom);

  const canDownloadReport = useRecoilValue(canDownloadReportAtom);

  const [progress, setProgress] = useState<DutyLinesProgress | null>(null);
  const [isInProgress, setIsInProgress] = useState<boolean>(false);
  const selectedChip = useRecoilValue(selectedChipAtom);

  const { mutate: realizeDutyLines } = useRealizeDutyLinesMutation();
  const { data, refetch, isLoading } = useDownloadRealizationReport({
    validFrom: dateRange[0],
    validTo: startOfDay(addDays(dateRange[1] as Date, 1))
  });

  const downloadReport = (data: ReportBody) => {
    if (!data || !data.downloadUrl) return;
    const link = document.createElement('a');
    link.href = data.downloadUrl;
    link.setAttribute(
      'download',
      `${t('duties')}-${formatByLocale(new Date())}.xlsx`
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleDownloadClick = () => {
    refetch();
  };

  const onRealizationProgress = (newProgress: DutyLinesProgress) => {
    if (newProgress?.hasFinished) {
      setIsInProgress(false);
      setProgress(newProgress);
      EventBus.unregister(eventBusUrl, onRealizationProgress);
    } else {
      setProgress(newProgress);
    }
  };

  const onChangeCheckbox = (evt, checked) => {
    setCloseAllPrevious(checked);
    setProgress(null);
  };

  const isCheckboxDisabled = () => {
    if (!dateRange[1] || !lastLockedMonth?.monthEnd) {
      return false;
    }

    const lastLockedDate = parse(
      lastLockedMonth.monthEnd,
      'yyyy-MM-dd',
      new Date()
    );

    return isBefore(dateRange[1] as Date, lastLockedDate);
  };

  const onClickRealize = async () => {
    if (!dateRange[0] || !dateRange[1]) {
      return;
    }

    setIsInProgress(true);
    EventBus.register(eventBusUrl, onRealizationProgress);

    realizeDutyLines(
      {
        startDate: dateRange[0] as Date,
        endDate: startOfDay(addDays(dateRange[1] as Date, 1)),
        lockMonth: closeAllPrevious
      },
      {
        onError: (error: unknown) => {
          const is503 =
            (error as { response?: { status?: number } })?.response?.status ===
            503;
          if (!is503) {
            setProgress(null);
            setIsInProgress(false);
            EventBus.unregister(eventBusUrl, onRealizationProgress);
          }
        }
      }
    );
  };

  const isValidDateRange =
    dateRange[0] &&
    dateRange[1] &&
    isValid(dateRange[0]) &&
    isValid(dateRange[1]);

  const dateRangeMisMatchError = Boolean(
    dateRange[0] &&
      dateRange[1] &&
      isAfter(dateRange[0] as Date, dateRange[1] as Date)
  );

  const futureWarning =
    dateRange[1] && isAfter(dateRange[1] as Date, new Date());

  useEffect(() => {
    if (data?.status === 'finished') {
      downloadReport(data);
    }
  }, [data]);

  useEffect(() => {
    if (selectedChip) {
      setProgress(null);
      setIsInProgress(false);
      EventBus.unregister(eventBusUrl, onRealizationProgress);
    }
  }, [selectedChip]);

  return (
    <StyledTile
      title={t('adminPortal.realization.realizationProcedure.name')}
      sx={{
        height: '100%',
        p: 2,

        opacity: isValidDateRange && !dateRangeMisMatchError ? 1 : 0.5
      }}
      data-testid="realization"
    >
      <Typography variant="body2" sx={{ fontFamily: 'SourceSansPro-Regular' }}>
        {t('adminPortal.realization.realizationProcedure.description')}
      </Typography>
      <Box pt={3}>
        <FormControlLabel
          disableTypography
          sx={{ fontSize: '13px', fontFamily: 'SourceSansPro-Regular' }}
          control={
            <Checkbox
              checked={closeAllPrevious}
              onChange={onChangeCheckbox}
              disabled={isCheckboxDisabled()}
            />
          }
          label={
            t('adminPortal.realization.realizationProcedure.lockDescription', {
              month: lastLockedMonth?.monthEnd
                ? format(
                    parse(lastLockedMonth.monthEnd, 'yyyy-MM-dd', new Date()),
                    'MM-yyyy'
                  )
                : '--'
            }) as string
          }
        />
      </Box>
      <Box pt={3}>
        {(!progress || progress?.hasFinished) && (
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              size="small"
              variant="tonal"
              onClick={onClickRealize}
              disabled={
                isInProgress || !isValidDateRange || dateRangeMisMatchError
              }
            >
              {t('adminPortal.realization.realize')}
            </Button>
            {isValidDateRange && !dateRangeMisMatchError && (
              <Box display="flex" alignItems="center" gap={1}>
                <Typography>
                  {`${formatByLocale(dateRange[0]! as Date)} ${t(
                    'until'
                  )} ${formatByLocale(dateRange[1]! as Date)}`}
                </Typography>
                {futureWarning && (
                  <Tooltip
                    title={
                      t(
                        'adminPortal.realization.realizationProcedure.warning'
                      ) || ''
                    }
                  >
                    <Icon type="triangle-exclamation" color="warning" />
                  </Tooltip>
                )}
              </Box>
            )}
          </Box>
        )}
        {progress && !progress.hasFinished && (
          <LinearProgress
            variant="determinate"
            value={(progress.dutyLinesProgress / progress.dutyLinesTotal) * 100}
          />
        )}
        {progress?.message.length === 0 && (
          <>
            <Typography mt={4}>
              {t('adminPortal.realization.realized')}:{' '}
              {progress.dutyLinesProgress}/{progress.dutyLinesTotal}{' '}
            </Typography>
            {hasTravelMovementsFeature && (
              <Typography>
                {t('adminPortal.realization.realizedTravelMovements')},{' '}
                {progress.dutyLinesErrors} {t('errors')}
              </Typography>
            )}
            {hasFeatureToggleExpenseClaims && (
              <Typography>
                {t('adminPortal.realization.realizedExpenseClaims')},{' '}
                {progress.dutyLinesErrors} {t('errors')}
              </Typography>
            )}
            {!hasTravelMovementsFeature && !hasFeatureToggleExpenseClaims && (
              <Typography>
                {progress.dutyLinesErrors} {t('errors')}
              </Typography>
            )}
          </>
        )}
        {progress?.dutyLinesErrors === 0 && progress?.message.length > 0 && (
          <Typography mt={4}>
            {progress.dutyLinesProgress}/{progress.dutyLinesTotal},{' '}
            {t(progress.message)}
          </Typography>
        )}
        {progress?.hasFinished &&
          progress?.dutyLinesErrors > 0 &&
          canDownloadReport && (
            <Box mt={2}>
              <Button
                size="small"
                variant="tonal"
                disabled={isLoading}
                onClick={handleDownloadClick}
              >
                {t('downloadExcelReport')}
              </Button>
            </Box>
          )}
      </Box>
    </StyledTile>
  );
};

export default RealizationProcedure;
